<template>
	<v-row>
		<v-dialog v-model="showDialog" scrollable persistent max-width="1050px" min-height="400px">
			<v-skeleton-loader v-if="loading" type="article, actions" />
			<v-card v-if="!loading">
				<v-card-title class="px-2 py-0">
					<v-col cols="12" class="d-flex justify-space-between align-center">
						<h4>
							{{ !data.id ? $t('modules.user.dialog.title_new') : $t('modules.user.dialog.title_edit') }}
						</h4>
						<v-btn icon @click="showDialog=false">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-col>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pb-0" @wheel="onWheel">
					<v-container>
						<v-form ref="form">
							<v-row class="mt-0">
								<v-col cols="6">
									<v-text-field
										v-model="data.firstname"
										:label="$t('modules.user.dialog.firstname')"
										autocomplete="none"
										:rules="[validationRules.required]"
										max="100"
									/>
								</v-col> 
								<v-col cols="6">
									<v-text-field
										v-model="data.lastname"
										:label="$t('modules.user.dialog.lastname')"
										autocomplete="none"
										:rules="[validationRules.required]"
										max="150"
									/>
								</v-col> 
								<v-col cols="6">
									<v-text-field
										v-model="data.email"
										:label="$t('modules.user.dialog.email')"
										autocomplete="none"
										:rules="[validationRules.required, validationRules.email]"
										max="250"
									/>
								</v-col>  
								<v-col cols="6" class="py-0">  
									<v-switch inset
										v-model="data.enabled"
										:label="$t('modules.user.dialog.enabled')"
										flat
										/>
								</v-col> 
							</v-row>
							<v-row class="mt-4">
								<v-col cols="12" class="pb-0">  
									<v-alert 
										v-if="showError" 
										type="error" 
										icon="mdi-alert">
										{{ textError }}
									</v-alert>
								</v-col> 
							</v-row>
						</v-form>
					</v-container>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-btn v-if="data.id && !isSessionUser" color="#ff0000" @click="confirmDelete()">
						<v-icon left color="#ffffff">mdi-delete</v-icon>
						<span style="color: #ffffff;">{{ $t('common.dialog-buttons.delete') }}</span>
					</v-btn>
					<v-btn :disabled="!isSameEmail" color="#185EA9" text @click="resetPassword()" style="font-size: 0.9em; font-weight: 400;">
						{{ $t('modules.user.dialog.reset_password') }}
					</v-btn>
					<v-spacer></v-spacer>
					<v-btn color="#C3271A" text @click="onSave()" style="font-size: 0.9em; font-weight: 400;">
						{{ $t('common.dialog-buttons.ok') }}
					</v-btn>
					<v-btn text @click="showDialog=false" style="font-size: 0.9em; font-weight: 400;">
						{{ $t('common.dialog-buttons.cancel') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>  
		<ConfirmDialog ref="dlgConfirm"
			:title="$t('common.confirm-dialog.title')"
			:message="$t('common.confirm-dialog.text')"
			:titleOk="$t('common.dialog-buttons.delete')"
			:titleCancel="$t('common.dialog-buttons.cancel')"
			@confirmed="deleteItem()" />
	</v-row>	
</template>
  
<script>
import BaseModule from '../common/BaseModule.js';
import ConfirmDialog from '../common/ConfirmDialog.vue';
import api from '../../api/humanway.js';

export default {
	name: 'UserDialog',
	mixins: [BaseModule],
	components: {
		ConfirmDialog
	},
	data() {
		return {
			showDialog: false,
			loading: false,
			showError: false,
			textError: '',
			user: null,
			data: {},
		}
	},
	computed: {
		isSessionUser() {
			if (!this.data.id) {
				return false;
			}
			return this.$store.state.humanway.profile.username === this.user.username;
		},
		isSameEmail() {
			if (!this.user) {
				return false;
			}
			return this.user.email && this.user.email.toLowerCase() === this.data.email.toLowerCase();
		}
	},
	methods: {
		onWheel(event) {
			event.stopPropagation();
		},
		open(action, payload) {
			this.user = null;
			this.showError = false;
			this.textError = '';

			this.showDialog = true;

			if (this.$refs.form) {
				this.$refs.form.resetValidation();
			}

			if (!payload) {
				this.data = {
					enabled: true,
				};
				return;
			}

			this.loading = true;

			api.getUser(payload, 
				res => {
					this.user = res;
					this.data = { ...this.user };
					this.loading = false;
				}, 
				error => {
					this.$log.error(error);
					this.$emit('show-alert', { text: error, color: 'error' });
					this.loading = false;
				}
			);
		},
		resetPassword() {
			this.loading = true;

			api.resetUserPassword(this.user.id, 
				res => {
					this.loading = false;				
					if (res) {
						this.$emit('show-alert', { text: this.$t('modules.user.dialog.reset_password_email_sent_info', [this.user.email]), color: 'success' });
					}
				},
				error => {
					this.$log.error(error);
					this.loading = false;
					this.$emit('show-alert', { text: error, color: 'error' });
				}
			);
		},
		existsUser(msgCb) {
			this.loading = true;

			api.existsUser(this.data, 
				res => {
					this.loading = false;
					if (res) {
						this.textError = this.$t('modules.user.dialog.already_exists_error');
						this.showError = true;
						return;
					}
					
					this.showError = false;
					this.textError = '';
					if (msgCb) {
						msgCb();
					}
				},
				error => {
					this.$log.error(error);
					this.loading = false;
					this.$emit('show-alert', { text: error, color: 'error' });
				}
			);
		},
		onSave() {
			if (!this.$refs.form.validate()) {
				return;
			}

			this.existsUser(() => {
				this.storeUser();
			});
		},
		storeUser() {
			this.loading = true;

			api.storeUser(this.data, 
				() => {
					this.loading = false;
					this.showDialog = false;
					this.$emit('refresh');
				},
				error => {
					this.$log.error(error);
					this.loading = false;
					this.$emit('show-alert', { text: error, color: 'error' });
				}
			);
		},
		confirmDelete() {
			this.$refs.dlgConfirm.confirm(this.data);
		},
		deleteItem() {
			this.loading = true;
			
			api.deleteUser(this.data.id, 
				() => {
					this.showDialog = false;
					this.loading = false;
					this.$emit('refresh');
				},
				error => {
					this.$log.error(error);
					this.loading = false;
					this.$emit('show-alert', { text: error, color: 'error' });
				}
			);
		}
	},
}
</script>